import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import BurgerButton from "../../UI/BurgerButton/BurgerButton";
import PopupMenu, { Overlay } from "../../UI/PopupMenu/PopupMenu";
import { logo, MetamaskLogo } from "../../../assets";
import { useWallet } from "../../../contexts/WalletContext";
import { useAuth } from "../../../contexts/AuthContext";
import "./NavBar.scss";
import { X } from "lucide-react";

const NavBar = () => {
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const handleNavBarRef = useRef(null);
  const [marginTop, setMarginTop] = useState(0);

  const location = useLocation();
  const {
    ethAddress: walletAddress,
    isConnecting,
    connectWallet,
  } = useWallet();
  const { isAuthenticated } = useAuth();
  useEffect(() => {
    const isMobile = window.innerWidth <= 600;

    if (isPopupOpen && isMobile) {
      const scrollY = window.scrollY;

      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";

      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";

        window.scrollTo(0, scrollY);
      };
    }
  }, [isPopupOpen]);
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;

      ticking = true;

      requestAnimationFrame(() => {
        const next = window.scrollY > 200;

        setIsScrolled((prev) => {
          if (prev === next) return prev;
          return next;
        });

        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (handleNavBarRef.current)
      setMarginTop(handleNavBarRef.current.clientHeight);
  }, [isPopupOpen]);

  const formatAddress = (address) =>
    `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;

  const handleWalletConnection = async () => {
    try {
      await connectWallet("MetaMask");
    } catch (error) {
      console.error("Failed to connect wallet:", error);
    }
  };

  // The auth page owns wallet sign-in through its own WalletLoginButton. This
  // button runs the same web3 login without leaving the page, so on /auth it is
  // a second, silent path into the flow the page is already presenting.
  const isAuthPage = location.pathname.startsWith("/auth");
  const showWalletChip = isAuthenticated && walletAddress;

  const showSubNav = ["/gallery", "/physicals", "/digitals"].some((path) =>
    location.pathname.startsWith(path),
  );
  const showSubNav2 =
    location.pathname.startsWith("/reproduction") ||
    location.pathname.startsWith("/physical-editions") ||
    location.pathname.startsWith("/posters") ||
    location.pathname === "/archive";

  const showSubNav3 =
    location.pathname.startsWith("/services") ||
    location.pathname.startsWith("/CoA")||
    location.pathname.startsWith("/verification");

  const showSubNav4 =
    location.pathname.startsWith("/about");

  return (
    <div className={`NavBar custom-height`}>
      <div
        className={`nav-bar-buf ${isScrolled ? "scrolled" : ""} ${
          showSubNav || showSubNav2 || showSubNav3 || showSubNav4 ? "no-shadow" : ""
        }`}
        ref={handleNavBarRef}
      >
        <div className="nav-container">
          <div className="nav-upper">
            <Link to="/">
              <div className="inter_active_icon">
                <div
                  className="nav-logo"
                  style={{
                    background: `url(${logo})`,
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                    transform: "scale(2)",
                  }}
                ></div>
                <div className="nav-seporator_icon"></div>
                <div className="nav-company_title">funooni</div>
              </div>
            </Link>

            <div className="burger-menu">
              {/* Guard the wrapper, not just the button - .burger-menu has a
                  gap, so an empty div would still push the burger icon over. */}
              {(showWalletChip || !isAuthPage) && (
                <div className="connect-metamask-div">
                  {showWalletChip ? (
                    <Link
                      to="/profile"
                      className="wallet-address"
                      title="Go to my profile"
                    >
                      <div className="wallet-display">
                        <img
                          src={MetamaskLogo}
                          alt="MetaMask"
                          className="wallet-icon"
                        />
                        <span>{formatAddress(walletAddress)}</span>
                      </div>
                    </Link>
                  ) : (
                    <button
                      className="connect-metamask-btn"
                      onClick={handleWalletConnection}
                      disabled={isConnecting}
                    >
                      {isConnecting ? "Connecting..." : "Connect Wallet"}
                    </button>
                  )}
                </div>
              )}

              <button
                onClick={() => setPopupOpen(!isPopupOpen)}
                className="burger-btn"
              >
                {isPopupOpen ? (
                  <X className="svg" size={24} />
                ) : (
                  <BurgerButton size={24} />
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="nav-stroke">
          <ul className="nav-link-container">
            <li className="nav-point">
              <Link to="/" className="nv-link type2">
                <p>Home</p>
              </Link>
            </li>
            <li className="nav-point">
              <Link to="/about" className="nv-link type2">
                <p>About Us</p>
              </Link>
            </li>
            <li className="nav-point">
              <Link to="/physicals" className="nv-link type2">
                <p>Gallery</p>
              </Link>
            </li>
            <li className="nav-point">
              <Link to="/creators" className="nv-link type2">
                <p>Creators</p>
              </Link>
            </li>
            <li className="nav-point">
              <Link to="/reproduction" className="nv-link type2">
                <p>Historical Project</p>
              </Link>
            </li>
            <li className="nav-point">
              <Link to="/media" className="nv-link type2">
                <p>Media</p>
              </Link>
            </li>
          </ul>
        </div>

        {showSubNav && (
          <div className="sub-nav">
            <ul className="sub-nav-links">
              <li>
                <Link
                  to="/physicals"
                  className={`sub-link ${
                    location.pathname.startsWith("/physicals") ? "active" : ""
                  }`}
                >
                  Physical
                </Link>
              </li>
              <li>
                <Link
                  to="/digitals"
                  className={`sub-link ${
                    location.pathname.startsWith("/digitals") ? "active" : ""
                  }`}
                >
                  Digital
                </Link>
              </li>
            </ul>
          </div>
        )}

        {showSubNav2 && (
          <div className="sub-nav">
            <ul className="sub-nav-links">
              <li>
                <Link
                  to="/reproduction"
                  className={`sub-link ${
                    location.pathname.startsWith("/reproduction")
                      ? "active"
                      : ""
                  }`}
                >
                  Artworks
                </Link>
              </li>
              <li>
                <Link
                  to="/archive"
                  className={`sub-link ${
                    location.pathname.startsWith("/archive") ? "active" : ""
                  }`}
                >
                  Archive
                </Link>
              </li>
                <li>
                <Link
                  to="/physical-editions"
                  className={`sub-link ${
                    location.pathname.startsWith("/physical-editions")
                      ? "active"
                      : ""
                  }`}
                >
                  Physical Editions
                </Link>
              </li>
                <li>
                <Link
                  to="/posters"
                  className={`sub-link ${
                    location.pathname.startsWith("/posters")
                      ? "active"
                      : ""
                  }`}
                >
                  Posters
                </Link>
              </li>
            </ul>
          </div>
        )}

        {showSubNav3 && (
          <div className="sub-nav">
            <ul className="sub-nav-links">
              <li>
                <Link
                  to="/services"
                  className={`sub-link ${
                    location.pathname.startsWith("/services") ? "active" : ""
                  }`}
                >
                  Services
                </Link>
              </li>
              <li>
                <Link
                  to="/CoA"
                  className={`sub-link ${
                    location.pathname.startsWith("/CoA") ? "active" : ""
                  }`}
                >
                  Certificates
                </Link>
              </li>
               <li>
                <Link
                  to="/verification"
                  className={`sub-link ${
                    location.pathname.startsWith("/verification") ? "active" : ""
                  }`}
                >
                  Verification
                </Link>
              </li>
            </ul>
          </div>
        )}

        {showSubNav4 && (
          <div className="sub-nav">
            <ul className="sub-nav-links">
              <li>
                <Link
                  to="/about"
                  className={`sub-link ${
                    location.pathname.startsWith("/about") ? "active" : ""
                  }`}
                >
                  About
                </Link>
              </li>
            </ul>
          </div>
        )}
      </div>

      {isPopupOpen && (
        <>
          <Overlay className="active" onClick={() => setPopupOpen(false)} />
          <PopupMenu
            marginTop={marginTop}
            togglePopup={() => setPopupOpen(false)}
          />
        </>
      )}
    </div>
  );
};

export default NavBar;